import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Divider,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	Grid,
	InputLabel,
	MenuItem,
	Select,
	Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import GroupsIcon from "@mui/icons-material/Groups";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import EventIcon from "@mui/icons-material/Event";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import SensorOccupiedIcon from "@mui/icons-material/SensorOccupied";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { useNavigate } from "react-router-dom";
import { LeyendaRecolectores } from "./LeyendaRecolectores";
import { AvatarPersona } from "../documentacion/AvatarPersona";
import { cpPorFoto } from "../../services/apis";

export const CuadrillaCard = ({ info, onRecolectorAvatarUploaded, onRefetchDetalle }) => {
	const navigate = useNavigate();
	const [fichajePreview, setFichajePreview] = useState(null);
	const [fichajePreviewSrc, setFichajePreviewSrc] = useState("");
	const [fichajePreviewLoading, setFichajePreviewLoading] = useState(false);
	const [fichajePreviewError, setFichajePreviewError] = useState("");
	const [cpUploading, setCpUploading] = useState(false);
	const [cpResult, setCpResult] = useState(null);
	const token =
		localStorage.getItem("accessToken") ||
		sessionStorage.getItem("accessToken") ||
		"";
	const fileInputRef = useRef(null);

	const idCuadrilla = info?.datos_cuadrilla?.id_cuadrilla ?? null;

	const suplantacionUi = useMemo(() => {
		const reason = typeof cpResult?.reason === "string" ? cpResult.reason : "";

		if (reason === "liveness_failed") {
			return {
				severity: "warning",
				title: "Liveness fallido",
				description: "La comprobación de vida no ha superado el umbral mínimo.",
			};
		}

		if (reason === "no_face") {
			return {
				severity: "warning",
				title: "No se detectó cara",
				description: "No se detectó una cara en la imagen enviada.",
			};
		}

		if (reason === "not_recognized") {
			return {
				severity: "error",
				title: "No reconocido",
				description: "No se encontró coincidencia válida para la foto.",
			};
		}

		if (reason === "recognized_not_registered") {
			return {
				severity: "error",
				title: "Reconocido pero no registrado",
				description: "Se reconoció a alguien, pero no existe en la base de datos.",
			};
		}

		if (reason === "no_pertenece_parte") {
			return {
				severity: "error",
				title: "No pertenece al parte",
				description: "Se reconoció a alguien que no está registrado en este parte de grupo.",
			};
		}

		return {
			severity: "error",
			title: "",
			description: "",
		};
	}, [cpResult?.reason]);

	const cpDetails = useMemo(() => {
		if (!cpResult) return [];

		const entries = [];

		if (typeof cpResult?.reason === "string" && cpResult.reason.trim()) {
			entries.push({ key: "reason", label: "reason", value: cpResult.reason.trim() });
		}

		if (cpResult?.liveness_score !== undefined && cpResult?.liveness_score !== null) {
			entries.push({
				key: "liveness_score",
				label: "liveness",
				value: String(cpResult.liveness_score),
			});
		}

		if (cpResult?.probability !== undefined && cpResult?.probability !== null) {
			entries.push({
				key: "probability",
				label: "prob.",
				value: String(cpResult.probability),
			});
		}

		if (typeof cpResult?.uuid_luxand === "string" && cpResult.uuid_luxand.trim()) {
			entries.push({
				key: "uuid_luxand",
				label: "uuid",
				value: cpResult.uuid_luxand.trim(),
			});
		} else if (typeof cpResult?.uuid === "string" && cpResult.uuid.trim()) {
			entries.push({ key: "uuid", label: "uuid", value: cpResult.uuid.trim() });
		}

		if (cpResult?.id_usuario_detectado !== undefined && cpResult?.id_usuario_detectado !== null) {
			entries.push({
				key: "id_usuario_detectado",
				label: "detectado",
				value: String(cpResult.id_usuario_detectado),
			});
		}

		return entries;
	}, [cpResult]);

	const getColor = (recolector) => {
		if (recolector.comprobado === 0) {
			return "primary";
		}

		if (recolector.comprobado === 1 && recolector.respuesta_comprobacion === 1) {
			return "secondary";
		}

		if (recolector.comprobado === 1 && recolector.respuesta_comprobacion === 0) {
			return "error";
		}

		return "default";
	};

	const getFichajeImageEndpoint = (recolector) => {
		if (typeof recolector?.url_imagen_endpoint === "string" && recolector.url_imagen_endpoint.trim()) {
			return recolector.url_imagen_endpoint.trim();
		}

		if (
			typeof recolector?.url_imagen !== "string" ||
			!recolector.url_imagen.trim() ||
			!recolector?.id_empleo
		) {
			return "";
		}

		return `https://recon.jcinformatics.com/imagefichaje.php?id_empleo=${recolector.id_empleo}`;
	};

	const getIntentoImageEndpoint = (intento) => {
		if (
			typeof intento?.url_imagen_endpoint === "string" &&
			intento.url_imagen_endpoint.trim()
		) {
			return intento.url_imagen_endpoint.trim();
		}

		const idIntento = Number(intento?.id_intento ?? 0);
		const imagenArchivo = typeof intento?.imagen_archivo === "string" ? intento.imagen_archivo.trim() : "";

		if (!Number.isFinite(idIntento) || idIntento <= 0 || !imagenArchivo) {
			return "";
		}

		return `https://recon.jcinformatics.com/imagefichaje.php?id_intento=${idIntento}`;
	};

	const totalRecolectores =
		info.recolectores.no_comprobados.length +
		info.recolectores.comprobados.length +
		info.recolectores.error_comprobacion.length;
	const intentosNoPertenece = Array.isArray(info?.intentos_no_pertenece)
		? info.intentos_no_pertenece
		: [];
	const intentosNoRegistrado = Array.isArray(info?.intentos_no_registrado)
		? info.intentos_no_registrado
		: [];

	const getPersonaAvatar = (recolector) => ({
		id: recolector.id_usuario,
		nombre: recolector.nombre_recolector,
		apellidos: recolector.apellidos_recolector,
		nombre_completo: `${recolector.nombre_recolector ?? ""} ${recolector.apellidos_recolector ?? ""}`.trim(),
		nif: recolector.nif_recolector,
		imagen_url: recolector.imagen_url || null,
	});

	const abrirDetallePersona = (recolector) => {
		if (!recolector?.id_usuario) return;

		navigate(`/personas/${recolector.id_usuario}`, {
			state: {
				persona: getPersonaAvatar(recolector),
				from: "/cuadrillas",
			},
		});
	};

	const abrirDetallePersonaDetectada = (intento) => {
		const idDetectado = Number(intento?.id_usuario_detectado ?? 0);
		if (!Number.isFinite(idDetectado) || idDetectado <= 0) return;

		const persona = {
			id: idDetectado,
			nombre: intento?.nombre_detectado ?? "",
			apellidos: intento?.apellidos_detectado ?? "",
			nombre_completo: `${intento?.nombre_detectado ?? ""} ${intento?.apellidos_detectado ?? ""}`.trim(),
			nif: intento?.nif_detectado ?? "",
			imagen_url: `https://recon.jcinformatics.com/image.php?id_usuario=${idDetectado}`,
		};

		navigate(`/personas/${idDetectado}`, {
			state: {
				persona,
				from: "/cuadrillas",
			},
		});
	};

	const abrirImagenFichaje = (recolector) => {
		if (!recolector) return;

		const endpoint = getFichajeImageEndpoint(recolector);
		if (!endpoint) return;

		const nombre = `${recolector.nombre_recolector ?? ""} ${recolector.apellidos_recolector ?? ""}`.trim();
		const puntuacion = recolector.puntuacion_comprobacion ?? "0.0";

		setFichajePreview({
			title: "Imagen del fichaje",
			nombre,
			subtitle: `${nombre || "Recolector"} - ${puntuacion}/10.0`,
			puntuacion,
			endpoint,
		});
	};

	const abrirImagenIntento = (intento, title = "Imagen del intento") => {
		if (!intento) return;

		const endpoint = getIntentoImageEndpoint(intento);
		if (!endpoint) return;

		const idIntento = Number(intento?.id_intento ?? 0);
		const idDetectado = Number(intento?.id_usuario_detectado ?? 0);
		const detectedNombre = `${intento?.nombre_detectado ?? ""} ${intento?.apellidos_detectado ?? ""}`.trim();
		const detectedNif = `${intento?.nif_detectado ?? ""}`.trim();

		const nombre = detectedNombre
			? `${detectedNombre}${detectedNif ? ` - ${detectedNif}` : ""}`
			: Number.isFinite(idDetectado) && idDetectado > 0
				? `Usuario #${idDetectado}`
				: Number.isFinite(idIntento) && idIntento > 0
					? `Intento #${idIntento}`
					: "Intento";

		const detailParts = [];
		if (intento?.probability !== undefined && intento?.probability !== null) {
			detailParts.push(`prob.: ${String(intento.probability)}`);
		}
		if (intento?.liveness_score !== undefined && intento?.liveness_score !== null) {
			detailParts.push(`liveness: ${String(intento.liveness_score)}`);
		}

		setFichajePreview({
			title,
			nombre,
			subtitle: detailParts.length ? `${nombre} - ${detailParts.join(" · ")}` : nombre,
			endpoint,
		});
	};

	const openFilePicker = () => {
		if (cpUploading) return;
		fileInputRef.current?.click();
	};

	const handleCpPorFoto = async (file) => {
		if (!file) return;
		if (!token) {
			setCpResult({
				success: false,
				reason: "token_missing",
				message: "No hay sesión activa.",
			});
			return;
		}

		if (!idCuadrilla) {
			setCpResult({
				success: false,
				reason: "missing_id_cuadrilla",
				message: "Falta id_cuadrilla.",
			});
			return;
		}

		setCpUploading(true);
		setCpResult(null);

		try {
			const resp = await cpPorFoto({ id_cuadrilla: idCuadrilla, foto: file });

			setCpResult(resp || null);

			if (resp?.success) {
				if (typeof onRefetchDetalle === "function") {
					await onRefetchDetalle();
				}
			}
		} catch (err) {
			console.error("Error cpPorFoto:", err);

			const backend = err?.response?.data;
			if (backend && typeof backend === "object") {
				setCpResult(backend);
			} else {
				setCpResult({
					success: false,
					reason: "network_error",
					message: "Error de red al comprobar la foto.",
				});
			}
		} finally {
			setCpUploading(false);
		}
	};

	const handleFileChange = async (event) => {
		const file = event.target.files?.[0];
		event.target.value = "";

		if (!file) return;

		if (!file.type.startsWith("image/")) {
			setCpResult({
				success: false,
				reason: "invalid_file",
				message: "Selecciona una imagen válida.",
			});
			return;
		}

		await handleCpPorFoto(file);
	};

	const cerrarImagenFichaje = () => {
		setFichajePreview(null);
	};

	useEffect(() => {
		let active = true;
		let objectUrl = null;

		const cargarImagenFichaje = async () => {
			if (!fichajePreview?.endpoint || !token) {
				setFichajePreviewSrc("");
				setFichajePreviewLoading(false);
				setFichajePreviewError("");
				return;
			}

			setFichajePreviewLoading(true);
			setFichajePreviewError("");
			setFichajePreviewSrc("");

			try {
				const separator = fichajePreview.endpoint.includes("?") ? "&" : "?";
				const endpoint = `${fichajePreview.endpoint}${separator}t=${Date.now()}`;
				const res = await fetch(endpoint, {
					headers: {
						Authorization: `Bearer ${token}`,
						"Cache-Control": "no-cache",
						Pragma: "no-cache",
					},
				});

				if (!res.ok) throw new Error("No se pudo cargar la imagen del fichaje");

				const blob = await res.blob();
				objectUrl = URL.createObjectURL(blob);

				if (active) {
					setFichajePreviewSrc(objectUrl);
				}
			} catch (error) {
				console.error("Error cargando imagen del fichaje:", error);
				if (active) {
					setFichajePreviewSrc("");
					setFichajePreviewError("No se pudo cargar la imagen del fichaje");
				}
			} finally {
				if (active) {
					setFichajePreviewLoading(false);
				}
			}
		};

		cargarImagenFichaje();

		return () => {
			active = false;
			if (objectUrl) URL.revokeObjectURL(objectUrl);
		};
	}, [fichajePreview?.endpoint, token]);

	const renderRecolector = (recolector, index, sectionKey, highlightError = false) => {
		const tieneImagenFichaje = Boolean(getFichajeImageEndpoint(recolector));

		return (
			<Grid
				key={`${sectionKey}-${recolector.id_empleo ?? index}`}
				container
				direction="row"
				justifyContent="start"
				alignItems="center"
				size={{ xs: 12, sm: 12, md: 6, lg: 3 }}
				sx={{ py: 1.5, rowGap: 1 }}
			>
				<Grid
					container
					direction="row"
					justifyContent="start"
					alignItems="center"
					size={{ xs: 12 }}
					sx={{ columnGap: 1, flexWrap: "nowrap" }}
				>
					<AvatarPersona
						persona={getPersonaAvatar(recolector)}
						token={token}
						size={32}
						allowUpload
						onAvatarUploaded={(updatedPersona) => {
							if (typeof onRecolectorAvatarUploaded === "function") {
								onRecolectorAvatarUploaded({
									idCuadrilla: info.datos_cuadrilla.id_cuadrilla,
									idUsuario: recolector.id_usuario,
									imagenUrl: updatedPersona.imagen_url,
								});
							}
						}}
					/>

					<Typography
						variant="body1"
						color={highlightError ? "error" : "inherit"}
						onClick={() => abrirDetallePersona(recolector)}
						sx={{
							fontWeight: highlightError ? 600 : 400,
							cursor: "pointer",
							"&:hover": {
								textDecoration: "underline",
								color: highlightError ? "error.main" : "primary.main",
							},
						}}
					>
						{recolector.nombre_recolector} {recolector.apellidos_recolector} -{" "}
						{recolector.nif_recolector}
					</Typography>
				</Grid>

				<Chip
					icon={<SensorOccupiedIcon />}
					label={
						!recolector.puntuacion_comprobacion
							? "0.0/10.0"
							: `${recolector.puntuacion_comprobacion}/10.0`
					}
					size="small"
					color={getColor(recolector)}
					clickable={tieneImagenFichaje}
					onClick={tieneImagenFichaje ? () => abrirImagenFichaje(recolector) : undefined}
					title={
						tieneImagenFichaje
							? "Ver imagen asociada al fichaje"
							: "Este fichaje no tiene imagen asociada"
					}
					sx={{
						cursor: tieneImagenFichaje ? "pointer" : "default",
					}}
				/>
			</Grid>
		);
	};

	const renderIntentoNoPertenece = (intento, index) => {
		const idIntento = intento?.id_intento ?? index;
		const idDetectado = Number(intento?.id_usuario_detectado ?? 0);
		const detectedNombre = `${intento?.nombre_detectado ?? ""} ${intento?.apellidos_detectado ?? ""}`.trim();
		const detectedNif = `${intento?.nif_detectado ?? ""}`.trim();
		const detectedLabel = detectedNombre
			? `${detectedNombre}${detectedNif ? ` - ${detectedNif}` : ""}`
			: Number.isFinite(idDetectado) && idDetectado > 0
				? `Usuario #${idDetectado}`
				: "";

		const intentoImageEndpoint = getIntentoImageEndpoint(intento);
		const tieneImagenIntento = Boolean(intentoImageEndpoint);

		return (
			<Grid
				key={`intento-no-pertenece-${idIntento}`}
				container
				direction="row"
				justifyContent="start"
				alignItems="center"
				size={{ xs: 12, sm: 12, md: 6, lg: 3 }}
				sx={{ py: 1.5, rowGap: 1 }}
			>
				<Grid
					container
					direction="row"
					justifyContent="start"
					alignItems="center"
					size={{ xs: 12 }}
					sx={{ columnGap: 1, flexWrap: "nowrap" }}
				>
					<Typography variant="body1" color="error" sx={{ fontWeight: 600 }}>
						No reconocido
					</Typography>
				</Grid>

				{detectedLabel && (
					<Typography
						variant="body2"
						color="text.secondary"
						onClick={() => abrirDetallePersonaDetectada(intento)}
						sx={{
							cursor:
								Number.isFinite(idDetectado) && idDetectado > 0 ? "pointer" : "default",
							"&:hover": {
								textDecoration:
									Number.isFinite(idDetectado) && idDetectado > 0 ? "underline" : "none",
								color:
									Number.isFinite(idDetectado) && idDetectado > 0
										? "primary.main"
										: "text.secondary",
							},
						}}
					>
						Detectado: {detectedLabel}
					</Typography>
				)}

				<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
					<Chip size="small" color="error" variant="outlined" label="no pertenece" />

					<Chip
						icon={<PhotoCameraIcon />}
						label="Foto"
						size="small"
						clickable={tieneImagenIntento}
						onClick={
							tieneImagenIntento
								? () => abrirImagenIntento(intento, "Imagen del intento (no pertenece al parte)")
								: undefined
						}
						title={
							tieneImagenIntento
								? "Ver foto asociada al intento"
								: "Este intento no tiene foto asociada"
						}
						sx={{
							cursor: tieneImagenIntento ? "pointer" : "default",
						}}
					/>

					{intento?.probability !== undefined && intento?.probability !== null && (
						<Chip
							size="small"
							variant="outlined"
							label={`prob.: ${String(intento.probability)}`}
						/>
					)}

					{intento?.liveness_score !== undefined && intento?.liveness_score !== null && (
						<Chip
							size="small"
							variant="outlined"
							label={`liveness: ${String(intento.liveness_score)}`}
						/>
					)}

					{typeof intento?.uuid_luxand === "string" && intento.uuid_luxand.trim() && (
						<Chip
							size="small"
							variant="outlined"
							label={`uuid: ${intento.uuid_luxand.trim()}`}
						/>
					)}
				</Box>
			</Grid>
		);
	};

	const renderIntentoNoRegistrado = (intento, index) => {
		const idIntento = intento?.id_intento ?? index;
		const intentoImageEndpoint = getIntentoImageEndpoint(intento);
		const tieneImagenIntento = Boolean(intentoImageEndpoint);

		return (
			<Grid
				key={`intento-no-registrado-${idIntento}`}
				container
				direction="row"
				justifyContent="start"
				alignItems="center"
				size={{ xs: 12, sm: 12, md: 6, lg: 3 }}
				sx={{ py: 1.5, rowGap: 1 }}
			>
				<Grid
					container
					direction="row"
					justifyContent="start"
					alignItems="center"
					size={{ xs: 12 }}
					sx={{ columnGap: 1, flexWrap: "nowrap" }}
				>
					<Typography variant="body1" color="error" sx={{ fontWeight: 600 }}>
						No reconocido
					</Typography>
				</Grid>

				<Typography variant="body2" color="text.secondary">
					Persona detectada no registrada en el sistema
				</Typography>

				<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
					<Chip size="small" color="error" variant="outlined" label="no registrado" />

					<Chip
						icon={<PhotoCameraIcon />}
						label="Foto"
						size="small"
						clickable={tieneImagenIntento}
						onClick={
							tieneImagenIntento
								? () =>
										abrirImagenIntento(
											intento,
											"Imagen del intento (no registrado en sistema)"
										)
								: undefined
						}
						title={
							tieneImagenIntento
								? "Ver foto asociada al intento"
								: "Este intento no tiene foto asociada"
						}
						sx={{
							cursor: tieneImagenIntento ? "pointer" : "default",
						}}
					/>

					{intento?.probability !== undefined && intento?.probability !== null && (
						<Chip
							size="small"
							variant="outlined"
							label={`prob.: ${String(intento.probability)}`}
						/>
					)}

					{intento?.liveness_score !== undefined && intento?.liveness_score !== null && (
						<Chip
							size="small"
							variant="outlined"
							label={`liveness: ${String(intento.liveness_score)}`}
						/>
					)}

					{typeof intento?.uuid_luxand === "string" && intento.uuid_luxand.trim() && (
						<Chip
							size="small"
							variant="outlined"
							label={`uuid: ${intento.uuid_luxand.trim()}`}
						/>
					)}
				</Box>
			</Grid>
		);
	};

	return (
		<Grid
			container
			direction="row"
			justifyContent="start"
			alignItems="center"
			size={{ xs: 12 }}
			sx={{
				p: 2,
				mb: 2,
			}}
		>
			<Grid
				container
				direction="row"
				justifyContent="space-between"
				alignItems="start"
				size={{ xs: 12 }}
			>
				{cpResult && (
					<Grid item size={{ xs: 12 }} sx={{ mb: 1 }}>
						<Alert
							severity={
								cpResult?.success
									? "success"
									: suplantacionUi?.title
										? suplantacionUi.severity
										: "error"
							}
						>
							<Typography variant="subtitle2" sx={{ mb: 0.25 }}>
								{cpResult?.success
									? "Validación por foto completada"
									: suplantacionUi?.title || "No se pudo validar por foto"}
							</Typography>
							<Typography variant="body2" sx={{ opacity: 0.85 }}>
								{cpResult?.message ||
									suplantacionUi?.description ||
									(cpResult?.success
										? "Se ha actualizado el estado del recolector."
										: "Revisa el motivo en el detalle.")}
							</Typography>

							{cpDetails.length > 0 && (
								<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
									{cpDetails.map((d) => (
										<Chip
											key={d.key}
											size="small"
											variant="outlined"
											label={`${d.label}: ${d.value}`}
										/>
									))}
								</Box>
							)}
						</Alert>
					</Grid>
				)}

				<Grid
					container
					direction="column"
					justifyContent="center"
					alignItems="start"
					size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
					gap={1}
				>
					<Grid
						container
						direction="row"
						justifyContent="start"
						alignItems="start"
						size={{ xs: 12 }}
					>
						<GroupWorkIcon sx={{ fontSize: 30 }} color="warning" />
						<Typography variant="h5">
							&nbsp;Grupo N: {info.datos_cuadrilla.id_cuadrilla}
						</Typography>
					</Grid>

					<Typography variant="h6">
						<EventIcon sx={{ mb: -1, fontSize: 30 }} color="primary" />{" "}
						{info.datos_cuadrilla.fecha_cuadrilla}
					</Typography>

					<Typography variant="subtitle1">
						<AssignmentIndIcon sx={{ mb: -1, fontSize: 30 }} color="secondary" /> Cabo:{" "}
						{info.datos_cuadrilla.nombre_cabo} {info.datos_cuadrilla.apellidos_cabo}
					</Typography>

					<Typography variant="subtitle1">
						<GroupsIcon sx={{ mb: -1, fontSize: 30 }} color="inherit" /> Recolectores:{" "}
						{totalRecolectores}
					</Typography>
				</Grid>

				<Grid
					container
					direction="column"
					justifyContent="start"
					alignItems={{ xs: "start", md: "end" }}
					size={{ xs: 12, sm: 12, md: 6, lg: 6, xl: 6 }}
					gap={1}
				>
					<Grid item size={{ xs: 8, md: 6, lg: 3 }}>
						<Button
							variant="contained"
							fullWidth
							startIcon={<PhotoCameraIcon />}
							disabled={!idCuadrilla || !token || cpUploading}
							onClick={openFilePicker}
							title={
								!token
									? "Inicia sesión para comprobar por foto"
									: !idCuadrilla
										? "Falta id_cuadrilla"
										: "Subir una foto para validar"
							}
						>
							{cpUploading ? "Comprobando..." : "Comprobar por foto"}
						</Button>
						<input
							ref={fileInputRef}
							type="file"
							accept="image/*"
							onChange={handleFileChange}
							style={{ display: "none" }}
						/>
					</Grid>

					<Grid item size={{ xs: 8, md: 6, lg: 1.5 }}>
						<FormControl variant="standard" fullWidth size="small">
							<InputLabel id="cuadrilla-validado-label">Validado</InputLabel>
							<Select
								labelId="cuadrilla-validado-label"
								id="cuadrilla-validado"
								label="Validado"
								defaultValue={info.datos_cuadrilla.validado}
							>
								<MenuItem value={0}>No</MenuItem>
								<MenuItem value={1}>Si</MenuItem>
							</Select>
						</FormControl>
					</Grid>

					<Grid item size={{ xs: 8, md: 6, lg: 1.5 }}>
						<FormControl variant="standard" fullWidth size="small">
							<InputLabel id="cuadrilla-revisado-label">Revisado</InputLabel>
							<Select
								labelId="cuadrilla-revisado-label"
								id="cuadrilla-revisado"
								label="Revisado"
								defaultValue={info.datos_cuadrilla.revisado}
							>
								<MenuItem value={0}>No</MenuItem>
								<MenuItem value={1}>Si</MenuItem>
							</Select>
						</FormControl>
					</Grid>

					<Grid item size={{ xs: 8, md: 6, lg: 1.5 }}>
						<Button variant="contained" fullWidth>
							actualizar
						</Button>
					</Grid>
				</Grid>
			</Grid>

			<Grid
				container
				direction="row"
				justifyContent="start"
				alignItems="start"
				size={{ xs: 12 }}
				sx={{ mt: 2 }}
			>
				<Grid
					container
					direction="row"
					justifyContent="start"
					alignItems="start"
					size={{ xs: 12 }}
					sx={{ display: info.recolectores.no_comprobados.length === 0 ? "none" : "flex" }}
				>
					<Typography variant="subtitle2">
						No comprobados ({info.recolectores.no_comprobados.length}):
					</Typography>

					<Divider />
					{info.recolectores.no_comprobados.map((recolector, index) =>
						renderRecolector(recolector, index, "no-comprobados")
					)}
				</Grid>

				<Grid
					container
					direction="row"
					justifyContent="start"
					alignItems="start"
					size={{ xs: 12 }}
					sx={{ display: info.recolectores.comprobados.length === 0 ? "none" : "flex" }}
				>
					<Typography variant="subtitle2">
						Comprobados ({info.recolectores.comprobados.length}):
					</Typography>

					<Divider />
					{info.recolectores.comprobados.map((recolector, index) =>
						renderRecolector(recolector, index, "comprobados")
					)}
				</Grid>

				<Grid
					container
					direction="row"
					justifyContent="start"
					alignItems="start"
					size={{ xs: 12 }}
					sx={{ display: info.recolectores.error_comprobacion.length === 0 ? "none" : "flex" }}
				>
					<Typography variant="subtitle2">
						Error en la comprobacion ({info.recolectores.error_comprobacion.length}):
					</Typography>

					<Divider />
					{info.recolectores.error_comprobacion.map((recolector, index) =>
						renderRecolector(recolector, index, "error-comprobacion", true)
					)}
				</Grid>

				<Grid
					container
					direction="row"
					justifyContent="start"
					alignItems="start"
					size={{ xs: 12 }}
					sx={{ display: intentosNoPertenece.length === 0 ? "none" : "flex" }}
				>
					<Typography variant="subtitle2">
						No reconocidos (no pertenece al parte) ({intentosNoPertenece.length}):
					</Typography>

					<Divider />
					{intentosNoPertenece.map((intento, index) =>
						renderIntentoNoPertenece(intento, index)
					)}
				</Grid>

				<Grid
					container
					direction="row"
					justifyContent="start"
					alignItems="start"
					size={{ xs: 12 }}
					sx={{ display: intentosNoRegistrado.length === 0 ? "none" : "flex" }}
				>
					<Typography variant="subtitle2">
						No reconocidos (no registrados en el sistema) ({intentosNoRegistrado.length}):
					</Typography>

					<Divider />
					{intentosNoRegistrado.map((intento, index) =>
						renderIntentoNoRegistrado(intento, index)
					)}
				</Grid>
			</Grid>

			<Divider />

			<Grid
				container
				direction="row"
				justifyContent="start"
				alignItems="end"
				size={{ xs: 12 }}
				sx={{ minHeight: "10vh" }}
			>
				<LeyendaRecolectores />
			</Grid>

			<Dialog open={Boolean(fichajePreview)} onClose={cerrarImagenFichaje} maxWidth="md" fullWidth>
				<DialogTitle>{fichajePreview?.title || "Imagen"}</DialogTitle>

				<DialogContent sx={{ display: "flex", justifyContent: "center", p: 2 }}>
					{fichajePreview && (
						<Grid container direction="column" spacing={2}>
							<Grid item size={{ xs: 12 }}>
								<Typography variant="subtitle1">
									{fichajePreview.subtitle || fichajePreview.nombre || "Recolector"}
								</Typography>
							</Grid>

							<Grid item size={{ xs: 12 }}>
								{fichajePreviewLoading ? (
									<Box
										sx={{
											minHeight: "35vh",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
										}}
									>
										<CircularProgress />
									</Box>
								) : fichajePreviewError ? (
									<Alert severity="error">{fichajePreviewError}</Alert>
								) : (
									<Box
										component="img"
										src={fichajePreviewSrc}
										alt={`Imagen de ${fichajePreview.nombre || "recolector"}`}
										sx={{
											maxWidth: "100%",
											maxHeight: "70vh",
											objectFit: "contain",
											borderRadius: 1,
										}}
									/>
								)}
							</Grid>
						</Grid>
					)}
				</DialogContent>

				<DialogActions>
					<Button onClick={cerrarImagenFichaje}>Cerrar</Button>
				</DialogActions>
			</Dialog>
		</Grid>
	);
};
